import { isBefore, intervalToDuration } from "date-fns";
import { dateFormat, dateFormatShort, zCivilite } from "model/schema";
import {
  AGE_MINIMUM,
  CHAMP_REQUIS,
  DATE_NAISSANCE_MIN,
  MESSAGE_ERROR_CHAMP_TROP_COURT,
  MESSAGE_ERROR_DATE_NAISSANCE_CONDUCTEUR_AGE_MINIMUM,
  MESSAGE_ERROR_DATE_NAISSANCE_SOUSCRIPTEUR_MINIMUM,
  MESSAGE_ERROR_FORMAT_CODE_ASSUREUR,
  MESSAGE_ERROR_FORMAT_DATE_NAISSANCE_CONDUCTEUR,
  MESSAGE_ERROR_FORMAT_NOM_CONDUCTEUR,
  MESSAGE_ERROR_FORMAT_PRENOM_CONDUCTEUR,
  MESSAGE_ERROR_FORMAT_RAISON_SOCIALE,
  MESSAGE_ERROR_FORMAT_RIE_NUMERO_POLICE,
  MESSAGE_ERROR_FORMAT_SIREN_SIRET,
  MESSAGE_ERROR_SIRET_OU_RAISON_SOCIALE_OBLIGATOIRE,
} from "utils/constantes";
import {
  regexCodeSociete,
  RIE_Nom_Type_restriction,
  RIE_Prenom_Type_restriction,
  RIE_LieuNaissance_restriction,
  regexSirenSiret,
  RIE_RaisonSociale_restriction,
  RIE_NumeroPolice_restriction,
} from "utils/regex";
import { isDateValid } from "utils/toDate";
import { zDate } from "utils/zDate";
import { z } from "zod";

export const criteresHorsParcParticulier = z.object({
  civilite: zCivilite,
  nom: z
    .string({ required_error: CHAMP_REQUIS })
    .min(3, MESSAGE_ERROR_CHAMP_TROP_COURT)
    .regex(RIE_Nom_Type_restriction, {
      message: MESSAGE_ERROR_FORMAT_NOM_CONDUCTEUR,
    }),
  prenom: z
    .string({ required_error: CHAMP_REQUIS })
    .min(3, MESSAGE_ERROR_CHAMP_TROP_COURT)
    .regex(RIE_Prenom_Type_restriction, {
      message: MESSAGE_ERROR_FORMAT_PRENOM_CONDUCTEUR,
    }),
  dateNaissance: zDate({
    format: [dateFormat, dateFormatShort],
    invalid_date: MESSAGE_ERROR_FORMAT_DATE_NAISSANCE_CONDUCTEUR,
    required_error: CHAMP_REQUIS,
  })
    .refine(
      (datePick) => {
        if (!datePick || typeof datePick === "string" || !isDateValid(datePick)) return true;
        return !isBefore(datePick, DATE_NAISSANCE_MIN);
      },
      {
        message: MESSAGE_ERROR_DATE_NAISSANCE_SOUSCRIPTEUR_MINIMUM,
      },
    )
    .refine(
      (datePick) => {
        try {
          if (!datePick || !isDateValid(datePick)) return true;
          const { years = 0 } = intervalToDuration({
            start: datePick,
            end: new Date(),
          });
          return years > AGE_MINIMUM;
        } catch (e) {
          console.log(e);
          return true;
        }
      },
      {
        message: MESSAGE_ERROR_DATE_NAISSANCE_CONDUCTEUR_AGE_MINIMUM,
      },
    ),

  lieuNaissance: z
    .string()
    .regex(RIE_LieuNaissance_restriction, {
      message: "erreur.CODE_INSEE_FORMAT",
    })
    .optional(),
  nomNaissance: z
    .string()
    .regex(RIE_Nom_Type_restriction, {
      message: "erreur.FORMAT_NOM_NAISSANCE",
    })
    .optional(),
  codeAssureur: z.string({ required_error: CHAMP_REQUIS }).regex(regexCodeSociete, {
    message: MESSAGE_ERROR_FORMAT_CODE_ASSUREUR,
  }),
});

export type CriteresHorsParcParticulier = z.infer<typeof criteresHorsParcParticulier>;

export const criteresHorsParcSociete = z
  .object({
    siretOuSiren: z.string().regex(regexSirenSiret, MESSAGE_ERROR_FORMAT_SIREN_SIRET).optional(),
    raisonSociale: z
      .string()
      .min(3, MESSAGE_ERROR_CHAMP_TROP_COURT)
      .regex(RIE_RaisonSociale_restriction, {
        message: MESSAGE_ERROR_FORMAT_RAISON_SOCIALE,
      })
      .optional(),
    numPolice: z
      .string()
      .regex(RIE_NumeroPolice_restriction, {
        message: MESSAGE_ERROR_FORMAT_RIE_NUMERO_POLICE,
      })
      .optional(),
    codeAssureur: z.string({ required_error: CHAMP_REQUIS }).regex(regexCodeSociete, {
      message: MESSAGE_ERROR_FORMAT_CODE_ASSUREUR,
    }),
  })
  .refine(
    (data) => {
      return data.siretOuSiren != null || data.raisonSociale != null;
    },
    {
      message: MESSAGE_ERROR_SIRET_OU_RAISON_SOCIALE_OBLIGATOIRE,
    },
  );

export type CriteresHorsParcSociete = z.infer<typeof criteresHorsParcSociete>;
